import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { ArrowRight, Atom, BookOpen, Trophy, Users, CheckCircle, Star, ChevronRight, GraduationCap } from 'lucide-react';

const Home = () => {
  const [heroData, setHeroData] = useState(null);
  const [heroBoardsData, setHeroBoardsData] = useState(null);
  const [aboutData, setAboutData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/site-sections/hero`),
      axios.get(`${API}/public/site-sections/hero_boards`).catch(() => ({ data: null })),
      axios.get(`${API}/public/site-sections/about_preview`),
      axios.get(`${API}/public/courses`),
      axios.get(`${API}/public/testimonials`),
      axios.get(`${API}/public/results`)
    ]).then(([hero, heroBoards, about, coursesRes, testimonialsRes, resultsRes]) => {
      setHeroData(hero.data);
      setHeroBoardsData(heroBoards.data);
      setAboutData(about.data);
      setCourses(coursesRes.data.slice(0, 3));
      setTestimonials(testimonialsRes.data.slice(0, 3));
      setResults(resultsRes.data.slice(0, 4));
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  const heroContent = heroData?.content || {};
  const heroBoardsContent = heroBoardsData?.content || {};
  const aboutContent = aboutData?.content || {};

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-deep-navy via-deep-blue to-deep-blue"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-electric-cyan/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-electric-cyan/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
                <Atom className="w-4 h-4 text-electric-cyan" />
                <span className="text-electric-cyan text-sm font-medium">Premium STEM Academy</span>
              </div>
              
              <h1 className="font-outfit text-5xl md:text-7xl font-bold text-white leading-none mb-6" data-testid="hero-headline">
                {heroContent.headline || 'Master Physics & Mathematics'}
              </h1>
              
              <p className="text-xl md:text-2xl text-electric-cyan font-medium mb-4">
                {heroContent.subheadline || 'Premium STEM Tutoring in Singapore'}
              </p>
              
              <p className="text-slate-400 text-lg leading-relaxed mb-8 max-w-xl">
                {heroContent.description || "Join Singapore's elite academy for IB, IGCSE, A-Level, and international board preparation."}
              </p>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/register" className="btn-primary flex items-center gap-2" data-testid="hero-cta-primary">
                  {heroContent.cta_primary || 'Start Your Journey'}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/courses" className="btn-secondary flex items-center gap-2" data-testid="hero-cta-secondary">
                  {heroContent.cta_secondary || 'Explore Courses'}
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-8">
                {(heroContent.stats || [
                  { value: '500+', label: 'Students Mentored' },
                  { value: '95%', label: 'Success Rate' },
                  { value: '10+', label: 'Years Experience' }
                ]).map((stat, idx) => (
                  <div key={idx}>
                    <div className="text-3xl font-outfit font-bold text-electric-cyan">{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-electric-cyan/20 rounded-3xl blur-3xl"></div>
                <div className="glass-card p-8 relative">
                  <div className="grid grid-cols-2 gap-4">
                    {(heroBoardsContent.boards || [
                      { name: 'IB', enabled: true },
                      { name: 'IGCSE', enabled: true },
                      { name: 'A-Level', enabled: true },
                      { name: 'O-Level', enabled: true }
                    ]).filter(b => b.enabled !== false).map((board, idx) => (
                      <div key={idx} className="bg-surface-navy/50 rounded-xl p-4 text-center">
                        <GraduationCap className="w-8 h-8 text-electric-cyan mx-auto mb-2" />
                        <span className="text-white font-medium">{board.name}</span>
                      </div>
                    ))}
                  </div>
                  {(heroBoardsContent.success_badge?.enabled !== false) && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-electric-cyan/20 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-electric-cyan" />
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            {heroBoardsContent.success_badge?.title || '95% Success Rate'}
                          </div>
                          <div className="text-slate-400 text-sm">
                            {heroBoardsContent.success_badge?.subtitle || 'Achieving target scores'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4">
              {aboutContent.title || 'Why Choose PhysicsCo?'}
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {aboutContent.subtitle || 'Excellence in STEM Education'}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(aboutContent.features || [
              { title: 'Expert Instruction', description: 'Learn from IIT-trained educators' },
              { title: 'Personalized Approach', description: 'Customized learning paths' },
              { title: 'Proven Results', description: '95% achieve target scores' },
              { title: 'Comprehensive Support', description: '24/7 doubt clearing' }
            ]).map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-electric-cyan" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Preview */}
      <section className="py-24 md:py-32 bg-deep-navy/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-between items-end mb-12"
          >
            <div>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4">Our Courses</h2>
              <p className="text-slate-400 text-lg">Comprehensive programs for every board</p>
            </div>
            <Link to="/courses" className="hidden md:flex items-center gap-2 text-electric-cyan hover:gap-3 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, idx) => (
              <Link
                key={course.id}
                to={`/courses/${course.id}`}
                className={`block select-none ${course.is_featured ? 'lg:col-span-2 lg:row-span-1' : ''}`}
                data-testid={`home-course-card-${course.id}`}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="glass-card p-6 h-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-electric-cyan/10 hover:border-electric-cyan/30 hover:-translate-y-1"
                >
                  {course.is_featured && (
                    <div className="inline-flex items-center gap-1 bg-electric-cyan/20 text-electric-cyan text-xs font-medium px-3 py-1 rounded-full mb-4">
                      <Star className="w-3 h-3" /> Featured
                    </div>
                  )}
                  <h3 className="font-outfit text-2xl font-semibold text-white mb-2">{course.name}</h3>
                  <p className="text-slate-400 mb-4 line-clamp-2">{course.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {course.grade_levels?.slice(0, 2).map((grade, i) => (
                      <span key={i} className="text-xs bg-surface-navy px-2 py-1 rounded text-slate-300">{grade}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <span className="text-2xl font-outfit font-bold text-electric-cyan">SGD {course.price}</span>
                      <span className="text-slate-400 text-sm ml-2">/{course.duration}</span>
                    </div>
                    <span className="text-electric-cyan bg-electric-cyan/10 px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1">
                      View <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
          
          <Link to="/courses" className="md:hidden flex items-center justify-center gap-2 text-electric-cyan mt-8">
            View All Courses <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4">Student Results</h2>
            <p className="text-slate-400 text-lg">Our students consistently achieve outstanding scores</p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {results.map((result, idx) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 text-center card-hover"
              >
                <div className="text-4xl font-outfit font-bold text-electric-cyan mb-2">{result.score}</div>
                <div className="text-white font-medium mb-1">{result.subject}</div>
                {result.improvement && (
                  <div className="text-green-400 text-sm">{result.improvement}</div>
                )}
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/results" className="btn-secondary inline-flex items-center gap-2">
              View All Results <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-deep-navy/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4">What Students Say</h2>
            <p className="text-slate-400 text-lg">Success stories from our community</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 card-hover"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-electric-cyan/20 flex items-center justify-center">
                    <span className="text-electric-cyan font-medium">{testimonial.student_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{testimonial.student_name}</div>
                    <div className="text-slate-400 text-sm">{testimonial.school} • {testimonial.grade}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-12 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-electric-cyan to-transparent"></div>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-4">
              Ready to Excel in STEM?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Join hundreds of students who have achieved their academic goals with PhysicsCo
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className="btn-primary flex items-center gap-2" data-testid="cta-register">
                Register Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
